import { useState, type FormEvent } from 'react';
import { Clock } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import {
  PERIOD_KIND_OPTIONS,
  periodRange,
  periodTitle,
} from '@/features/teacher/components/timetable/timetableCopy';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { PeriodKind, TimetablePeriod } from '@/types/timetable';
import { instituteTimetableApi } from '../api/timetable.api';
import { describeMinutes, durationMinutes, periodNumberToSend } from '../timetableWeek';

export interface PeriodTimingModalProps {
  period: TimetablePeriod;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Corrects one row of the institute's day.
 *
 * The whole row is sent every time rather than a patch of changed keys, which
 * is what the API takes and for the reason it says: a partial merge cannot tell
 * "clear the name" from "did not mention it".
 *
 * Turning a period that still holds lessons into a break is refused by the
 * server — the lessons would survive in a row the grid no longer offers to
 * edit — and the refusal is shown as it arrives.
 */
export function PeriodTimingModal({ period, onClose, onSaved }: PeriodTimingModalProps) {
  const [kind, setKind] = useState<PeriodKind>(period.kind);
  const [periodNumber, setPeriodNumber] = useState(String(period.periodNumber));
  const [label, setLabel] = useState(period.label ?? '');
  const [startTime, setStartTime] = useState(period.startTime);
  const [endTime, setEndTime] = useState(period.endTime);
  const [isSaving, setIsSaving] = useState(false);

  const isBreak = kind === 'BREAK';
  const duration = durationMinutes(startTime, endTime);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (isSaving) return;

    const trimmedLabel = label.trim();
    if (isBreak && !trimmedLabel) {
      toast.error('Name the break', 'A break needs a name — it has no number to go by.');
      return;
    }

    setIsSaving(true);
    try {
      await instituteTimetableApi.updatePeriod(period.uuid, {
        periodNumber: periodNumberToSend(periodNumber, isBreak),
        kind,
        label: trimmedLabel || undefined,
        startTime,
        endTime,
      });
      toast.success(
        'Period updated',
        `${periodRange(startTime, endTime)} — every class now uses these times.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      toast.error(
        'Could not update that period',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Edit ${periodTitle(period)}`}
      description="Bell times are shared by every class in the institute."
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="period-timing-form"
            isLoading={isSaving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            Save changes
          </Button>
        </>
      }
    >
      <form id="period-timing-form" onSubmit={save} className="flex flex-col gap-4 py-1">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Type"
            options={PERIOD_KIND_OPTIONS}
            value={kind}
            onChange={(event) => setKind(event.target.value as PeriodKind)}
          />
          {/* A break keeps whatever number it has; there is nothing to choose. */}
          {!isBreak && (
            <Input
              label="Period number"
              type="number"
              min={1}
              max={20}
              required
              value={periodNumber}
              onChange={(event) => setPeriodNumber(event.target.value)}
            />
          )}
        </div>

        <Input
          label={isBreak ? 'Name' : 'Name (optional)'}
          placeholder={isBreak ? 'e.g. Lunch break' : 'e.g. Assembly'}
          maxLength={40}
          required={isBreak}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          hint={isBreak ? undefined : `Left empty it reads as "Period ${periodNumber || '3'}".`}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Starts"
            type="time"
            required
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          />
          <Input
            label="Ends"
            type="time"
            required
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            error={duration <= 0 ? 'A period cannot end before it starts' : undefined}
          />
        </div>

        <p className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>
            {periodRange(startTime, endTime)} · {describeMinutes(duration)}
          </span>
        </p>
      </form>
    </Modal>
  );
}
